﻿import $ from 'jquery';
import { ElementPosition } from "../utilities/elementposition";
import { KeyCode } from '../utilities/keycode';
import SuppressesEvent from '../utilities/suppressesevent';

declare global {
  interface JQuery {
    popover(option?: any, args?: any): JQuery;
  }
}

export interface IPopoverOptions {
  popover: string;
  position: string;
  trigger: string;
  alignment: string;
  placement: string;
  containerClass: string;
  channel: string;
  sizing: string;
  mode: string;
  group: string;
  getContent: Function;
}

export class PopOver {

  private isOpen: boolean;
  private isEnabled: boolean;
  protected $element: JQuery;

  public static wireup: string = '[data-popover]';
  private static globalMenuChannels: object;

  public static DEFAULTS: IPopoverOptions = {
    popover: null,
    position: 'element',
    trigger: 'click',
    alignment: 'left',
    placement: 'bottom',
    containerClass: null,
    channel: 'default',
    sizing: 'default',
    mode: 'copy',
    group: null,
    getContent: function (popover: PopOver) {

      var $content = $('[data-popover-content="' + popover.options.popover + '"]').first();
      if ($content.length == 0)
        throw new Error('Popover content could not be found, be sure your popover option value matches some [data-popover-content] attribute value');

      var $children;

      if (popover.options.mode == 'copy') {
        $children = $content.children().clone(true);
      }
      else if (popover.options.mode == 'detach') {
        $children = $content.children().detach();
      }

      PopOver.sizeMenu(popover, PopOver.getGlobalMenu(popover), $children);

      return $children;
    }
  };

  constructor(element: JQuery, protected options: IPopoverOptions) {
    this.$element = $(element);
    this.isOpen = false;
    this.isEnabled = true;
    this.options = this.getOptions(options);

    this.init();
  }

  protected init() {
    if (!this.options.popover)
      throw new Error('Popover requires data-popover attribute on invoking element or "popover" value in options');

    if (this.options.mode == 'detach' && this.options.channel == PopOver.DEFAULTS.channel)
      throw new Error('A detached popover must use a custom channel');

    var self = this;
    self.$element.attr('data-popover-key', this.options.popover);

    if (this.options.trigger == 'click')
      this.$element.on('click.rustig.popover', $.proxy(this.open, this));
    else if (this.options.trigger == 'focus') {
      this.$element.on('focus.rustig.popover onfocus.rustig', $.proxy(this.open, this));
      $(document).on('focusout.rustig.popover', function (e) {
        self.close(e);
      });
    }
    else if (this.options.trigger == 'focusin') {
      this.$element.on('focusin.rustig.popover', $.proxy(this.open, this));
      $(document).on('focusout.rustig.popover', function (e) {
        self.close(e);
      });
    }
    else if (this.options.trigger == 'hover')
      this.$element.on('mouseover.rustig.popover', $.proxy(this.open, this));
    else if (this.options.trigger == 'contextmenu') {
      this.options.position = 'point';
      this.$element.on('mousedown.rustig.popover', $.proxy(this.openContextMenu, this));
    }

    $(document).on('click.rustig.popover.clickoff clickfired.rustig.popover.clickoff drag.rustig.popover show.rustig.modal', $.proxy(this.onClickOff, this));
    $(window).on('resize.rustig.popover closeglobalmenu.rustig.popover.' + this.options.channel, function (e) {
      self.close(e);
    });
    document.addEventListener('closeallpopovers', (e) => self.close(e));

    $(document).on('keyup.rustig.popover', (e) => {
      if (e.keyCode == KeyCode.Escape && self.isOpen) {
        e.stopImmediatePropagation();
        PopOver.closeAllPopOvers();
        new SuppressesEvent().execute('focus', self.$element, () => {
          self.$element.focus();
        });
      }
    });
  }

  public getDefaults(): IPopoverOptions {
    return PopOver.DEFAULTS;
  }

  public getOptions(options: IPopoverOptions): IPopoverOptions {
    let result = $.extend({}, this.getDefaults(), this.$element.data(), options);
    result.group = result.group || result.popover;

    return result;
  }

  public openContextMenu(e) {
    if (e && e.button != 2)
      return;

    PopOver.preventBrowserContextMenu();
    this.open(e);
  }

  public open = function (e) {
    var self = this;
    if (!self.isEnabled)
      return;

    if (self.options.trigger == 'click')
      $(document).trigger('clickfired.rustig.popover.clickoff', self);

    var isOpenPopoverTriggeredViaClick = self && self.isOpen && self.options.trigger == 'click';
    if (isOpenPopoverTriggeredViaClick) {
      self.close();
      return false;
    }

    PopOver.closeAllPopOversWithChannel(self.options.channel);

    if (self.isOpen)
      return false;

    self.$element.trigger('show.rustig.popover', { channel: self.options.channel });
    PopOver.buildGlobalMenu(self, e);

    PopOver.getGlobalMenu(self).show();
    self.isOpen = true;
    self.$element.trigger('shown.rustig.popover', { channel: self.options.channel });

    return false;
  };

  public close = function (e?: any) {
    if (!this.isOpen)
      return;

    this.$element.trigger('hide.rustig.popover', { channel: this.options.channel });
    this.isOpen = false;
    var $menu = PopOver.getGlobalMenu(this).hide();
    PopOver.emptyMenu(this, $menu);
    this.$element.trigger('hidden.rustig.popover', { channel: this.options.channel });
  }

  public onClickOff(e, popover: PopOver) {
    var eventTriggeredByMe = popover && this === popover
    var eventTriggeredByMyGroup = popover && popover.options && this.options.group && this.options.group === popover.options.group;
    var otherPopoverNoClickSetToMyGroup = popover && popover.$element && popover.$element.data('noclickoff') === this.options.group;

    if (!this.isOpen || eventTriggeredByMe || eventTriggeredByMyGroup || otherPopoverNoClickSetToMyGroup)
      return;

    var $target = $(e.target);
    var $noClickOff = $target.closest('[data-noclickoff]');
    var clickInPopover = $target.closest('#popoverGlobalMenu' + 'channel'.asDataSelector(this.options.channel)).length;
    var targetInAction = $target.closest('a, button, [data-url]').length;
    var targetInClickOffOverrideForChannel = clickInPopover && $noClickOff.length;
    var targetNoClickSetToMyGroup = $noClickOff.data('noclickoff') === this.options.group;
    var popoverTriggeredByFocusAndElementHasFocus = (this.options.trigger == 'focus' && this.$element.is(':focus')) || (this.options.trigger == 'focusin' && this.$element.find(':focus').addBack(':focus'));

    var shouldKeepPopoverOpen = targetInClickOffOverrideForChannel
      || targetNoClickSetToMyGroup
      || popoverTriggeredByFocusAndElementHasFocus
      || (clickInPopover && !targetInAction)

    if (shouldKeepPopoverOpen)
      return;

    this.close(e);
  }

  public disable(): void {
    this.close();
    this.isEnabled = false;
  }
  public enable(): void {
    this.isEnabled = true;
  }
  

  // HELPER FUNCTIONS
  // =========================
  public static preventBrowserContextMenu() {
    $(document).one('contextmenu', function () { return false; });
  }
  public static buildGlobalMenu(popover, e) {
    var $globalMenu = PopOver.getGlobalMenu(popover);
    if (!$globalMenu) {
      $globalMenu = $('<nav>').attr('id', 'popoverGlobalMenu')
                              .attr('data-channel', popover.options.channel)
                              .css({ top: 0, left: 0 });
      $(document.body).append($globalMenu);
      PopOver.globalMenuChannels[popover.options.channel] = $globalMenu;
    }

    PopOver.emptyMenu(popover, $globalMenu);

    if (popover.options.containerClass)
      $globalMenu.addClass(popover.options.containerClass);

    $.when(popover.options.getContent(popover)).done(function (data) {
      var isCopy = popover.options.mode == 'copy';
      var isDetachAndMenuIsEmpty = popover.options.mode == 'detach' && $globalMenu.children().length == 0;

      if (isCopy || isDetachAndMenuIsEmpty) {
        let focusGuard = '<div data-keyboard-focus-bound="on" tabindex="0"></div>';
        $globalMenu.append(focusGuard);
        $globalMenu.append(data);
        $globalMenu.append(focusGuard);
      }

      var position = new ElementPosition($globalMenu);
      if (e && popover.options.position == 'point')
        position.setPositionByPoint({ top: e.pageY, left: e.pageX });
      else
        position.setPositionByElement(popover.$element, popover.options.alignment, popover.options.placement);

      return $globalMenu;
    });
  }
  public static emptyMenu(popover, $globalMenu) {
    $globalMenu.removeAttr('class');
    $globalMenu.removeAttr('width');

    if (popover.options.mode == 'copy') {
      $globalMenu.children().remove();
    }
  }
  public static sizeMenu(popover, $globalMenu, $content) {
    if (popover.options.sizing === 'default')
      return;

    var $sizeElement = popover.options.sizing === 'element' ? popover.$element : $(popover.options.sizing);
    var width = $sizeElement[0].getBoundingClientRect().width;
    $content.css('min-width', '0px');
    $globalMenu.width(width);
  }
  public static closeAllPopOversWithChannel(channel) {
    $(window).trigger('closeglobalmenu.rustig.popover.' + channel);
  }

  public static closeAllPopOvers() {
    document.trigger('closeallpopovers');
  }

  public static getGlobalMenu(popover) {
    if (!popover)
      throw new Error('popover cannot be null');
    if (!PopOver.globalMenuChannels)
      PopOver.globalMenuChannels = {};

    return PopOver.globalMenuChannels[popover.options.channel];
  }


}

export class IPopOverHTMLElement extends HTMLElement {
  public popovers: any;
}

($ => {
  function Plugin(option, args) {
    return this.each(function () {
      let element = <IPopOverHTMLElement>this;
      element.popovers = element.popovers || {};

      var $this = $(this);

      var channel = $this.data('channel') || 'default';
      if (option) {
        if (typeof option == 'string')
          channel = args || channel;
        else
          channel = option.channel || channel;
      }
      var namespace = 'rustig.popover.' + channel;

      var data = $this.data(namespace);
      var options = typeof option == 'object' && option;

      if (!data) {
        data = new PopOver(this, options);
        $this.data(namespace, data);
        element.popovers[channel] = data;
      }
      if (typeof option == 'string')
        data[option]();
    });
  }

  $.fn.popover = Plugin;

})($)